// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";

import { IState } from "states/states";
import { IAction } from "actions";
import { formatTimestamp, roundToDigits } from "shared/utils/utils";
import { IRegulation, ITariffCircuit } from "shared/types/parameter";
import { IControlData, IControlDataRow } from "states/datastates";
import { isSet } from "shared/utils/utils";

import cssContent from "constants.module.scss";
import IconMonitor from "icons/iconMonitor";
import { formatPowerValue } from "guiutils";

const USE_NON_KILO_UNIT = false; // former useNonKiloUnit not supported yet

interface IMonitorGraphLegendExtProps {
  controlCircuitIndex: number | undefined;
  isDashboard: boolean;
}

type IMonitorGraphLegendProps = {
  curPower: number;
  targetValue: number | undefined;
  regulationPriorityStatus: number | undefined;
  trendLevel: number | undefined;
  values: IControlDataRow[] | undefined;

  controlParameter: IRegulation | undefined;
  tariffParameter: ITariffCircuit | undefined;
};

const mapStateToProps = (state: IState, ownProps: IMonitorGraphLegendExtProps) => {
  const controlParameter = state.para.parameter?.regulation[ownProps.controlCircuitIndex!];
  const tlId = controlParameter?.tariffline;
  const tlIndex = state.para.parameter?.tariffline.findIndex(
    (curItem, curIndex) => tlId === (curItem.id ?? curIndex),
  );

  const props: IMonitorGraphLegendProps = {
    curPower: state.data.tariffcircuit[tlIndex!]?.power ?? NaN,
    targetValue: state.data.controlcircuit[ownProps.controlCircuitIndex!]?.targetValue,
    regulationPriorityStatus:
      state.data.controlcircuit[ownProps.controlCircuitIndex!]?.regulationPriorityStatus,
    trendLevel: state.data.controlcircuit[ownProps.controlCircuitIndex!]?.trendLevel,
    values: state.data.controlcircuit[ownProps.controlCircuitIndex!]?.values,
    controlParameter,
    tariffParameter: state.para.parameter?.tariffline[tlIndex!],
  };
  return props;
};

interface IMonitorGraphLegendDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IMonitorGraphLegendDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

class MonitorGraphLegendComponent extends React.Component<
  IMonitorGraphLegendProps & IMonitorGraphLegendExtProps & IMonitorGraphLegendDispatch
> {
  public render() {
    // check for existing data
    if (
      !isSet(this.props.controlCircuitIndex) ||
      !isSet(this.props.tariffParameter) ||
      !isSet(this.props.targetValue) ||
      !this.props.tariffParameter?.regulationPeriod ||
      !this.props.values?.length
    ) {
      return <></>;
    }

    const regPeriod = this.props.tariffParameter.regulationPeriod;
    const curVal = this.props.values[this.props.values.length - 1];
    const curAccPower = (curVal.energy * 3600) / regPeriod;
    const trendValue = curAccPower + this.props.curPower * (1 - curVal.timestamp / regPeriod);

    return (
      <table id="monitorgraphlegend">
        <tbody>
          <tr>
            <td>
              <IconMonitor />
            </td>
            <td>
              <FormattedMessage id="Cummulated Power" />
            </td>
            <td className="value-col">
              {formatPowerValue(curAccPower, this.props.tariffParameter?.unit)}
            </td>
          </tr>
          <tr>
            <td>
              <svg style={{ marginTop: 4 }}>
                <line
                  x1="0"
                  y1="3"
                  x2="100"
                  y2="3"
                  style={{
                    stroke: cssContent.GRAPH_COLOR_TREND,
                    strokeWidth: 3,
                    strokeDasharray: "4,3",
                  }}
                />
              </svg>
            </td>
            <td>
              <FormattedMessage id="Trended Power" />
            </td>
            <td className="value-col">
              {formatPowerValue(trendValue, this.props.tariffParameter?.unit)}
            </td>
          </tr>
          <tr>
            <td>
              <svg style={{ marginTop: 4 }}>
                <line
                  x1="0"
                  y1="3"
                  x2="100"
                  y2="3"
                  style={{ stroke: cssContent.GRAPH_COLOR_TARGET, strokeWidth: 3 }}
                />
              </svg>
            </td>
            <td>
              <FormattedMessage id="Target Value" />
            </td>
            <td className="value-col">
              {formatPowerValue(this.props.targetValue, this.props.tariffParameter?.unit)}
            </td>
          </tr>
          <tr>
            <td>
              <svg style={{ marginTop: 4 }}>
                <line
                  x1="0"
                  y1="3"
                  x2="100"
                  y2="3"
                  style={{ stroke: cssContent.GRAPH_COLOR_CUR_TIMETAMP, strokeWidth: 3 }}
                />
              </svg>
            </td>
            <td>
              <FormattedMessage id="Period Runtime" />
            </td>
            <td className="value-col">{formatTimestamp(curVal.timestamp)}</td>
          </tr>
          <tr>
            <td></td>
            <td>
              <FormattedMessage id="Current Power" />
            </td>
            <td className="value-col">
              {formatPowerValue(this.props.curPower, this.props.tariffParameter?.unit)}
            </td>
          </tr>
          {!this.props.isDashboard && (
            <tr>
              <td></td>
              <td>
                <FormattedMessage id="Control Level" />
              </td>
              <td className="value-col">
                S{this.props.regulationPriorityStatus} / T{this.props.trendLevel}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }

  private formatValue(value: number) {
    const scaledVal = USE_NON_KILO_UNIT ? value * 1000 : value;
    return `${roundToDigits(scaledVal, 3)} ${this.props.tariffParameter?.unit}`;
  }
}

const MonitorGraphLegend = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MonitorGraphLegendComponent);

export default MonitorGraphLegend;
